import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { session, user, refresh, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("No invitation token provided"); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invitations/lookup?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) { const j = await r.json(); throw new Error(j.detail || "Invitation not found"); }
        return r.json();
      })
      .then(setInvite)
      .catch((e) => setError(e.message));
  }, [token]);

  const accept = async () => {
    if (!session) { navigate(`/login?redirect=/accept-invite?token=${token}`); return; }
    if (user.email.toLowerCase() !== invite.email.toLowerCase()) {
      toast.error(`This invitation is for ${invite.email}. Sign in with that email.`);
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc("accept_invitation", { invite_token: token });
      if (error) throw new Error(error.message);
      await refresh();
      toast.success("Joined organization");
      navigate("/app");
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] p-6">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#E8E2D9] p-10 text-center">
        <h1 className="font-display font-bold text-2xl">Invitation problem</h1>
        <p className="text-sm text-[#57534E] mt-2">{error}</p>
        <Link to="/login" className="inline-block mt-6 text-sm text-[#E8712C]">Go to sign in →</Link>
      </div>
    </div>
  );
  if (!invite) return <div className="min-h-screen flex items-center justify-center">Loading invitation…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] p-6" data-testid="accept-invite-page">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#E8E2D9] p-10">
        <div className="inline-block px-3 py-1 rounded-full bg-[#FDE9D8] text-[#E8712C] text-xs font-medium mb-4">Invitation</div>
        <h1 className="font-display font-bold text-2xl tracking-tight mb-2">Join {invite.organizations?.name}</h1>
        <p className="text-sm text-[#57534E] mb-6">
          You've been invited to join <b>{invite.organizations?.name}</b> as <b>{invite.role.replace("_", " ")}</b>.
        </p>
        <div className="p-4 bg-[#FAF8F5] rounded-xl border border-[#E8E2D9] text-sm mb-6 space-y-1">
          <div><span className="text-[#A8A29E]">Invited email:</span> {invite.email}</div>
          <div><span className="text-[#A8A29E]">Role:</span> {invite.role.replace("_", " ")}</div>
          <div><span className="text-[#A8A29E]">Expires:</span> {new Date(invite.expires_at).toLocaleDateString()}</div>
        </div>
        {!session ? (
          <div className="space-y-3">
            <p className="text-sm text-[#57534E]">Sign in or create an account with <b>{invite.email}</b> to accept.</p>
            <div className="flex gap-2">
              <Link to={`/login?redirect=/accept-invite?token=${token}`} className="flex-1"><Button data-testid="accept-signin-btn" className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Sign in</Button></Link>
              <Link to={`/signup?redirect=/accept-invite?token=${token}`} className="flex-1"><Button data-testid="accept-signup-btn" variant="outline" className="w-full rounded-full">Create account</Button></Link>
            </div>
          </div>
        ) : (
          <Button data-testid="accept-invite-submit" onClick={accept} disabled={busy}
                  className="w-full rounded-full bg-[#E8712C] hover:bg-[#C55A1F] py-6">
            {busy ? "Joining…" : `Accept as ${user.email}`}
          </Button>
        )}
      </div>
    </div>
  );
}
